/**
 * Tax calculation utilities for billable items
 *
 * Phase 5 Batch 4: Category-based tax suggestions
 * Matches backend logic in services/project-tracker/backend/schemas/billable_items.py
 */

export type BillableItemCategory =
  | 'professional_fee'
  | 'add_service'
  | 'billable_expense'
  | 'pass_through';

const GST_RATE = 0.05; // 5% GST for revenue categories

/**
 * Calculate suggested tax amount based on category and amount
 *
 * Rules:
 * - professional_fee: 5% GST (revenue)
 * - add_service: 5% GST (revenue)
 * - billable_expense: No tax (not revenue)
 * - pass_through: No tax (not revenue)
 *
 * @param amount - The billable item amount
 * @param category - The billable item category
 * @returns Suggested tax amount, rounded to 2 decimal places
 */
export function calculateSuggestedTax(
  amount: number,
  category: BillableItemCategory
): number {
  // Handle edge cases
  if (amount <= 0) {
    return 0;
  }

  // Revenue categories: apply 5% GST
  if (category === 'professional_fee' || category === 'add_service') {
    const tax = amount * GST_RATE;
    // Round to 2 decimal places
    return Math.round(tax * 100) / 100;
  }

  // Non-revenue categories: no tax
  return 0;
}

/**
 * Format tax suggestion message for UI display
 *
 * @param suggestedTax - The calculated suggested tax
 * @param category - The billable item category
 * @returns Formatted suggestion text
 */
export function formatTaxSuggestion(
  suggestedTax: number,
  category: BillableItemCategory
): string {
  if (category === 'professional_fee' || category === 'add_service') {
    return `Suggested: $${suggestedTax.toFixed(2)} (5% GST)`;
  }
  return 'No tax for this category';
}
